import { n, pct } from '../format'

/**
 * Breakdown cards.
 *
 * Form: each card is one series (row count per value) across many long-named,
 * ranked categories — so it is a table with an inline horizontal bar, not a
 * chart with a legend. Magnitude gets ONE hue (never a colour per row, which
 * would encode rank as identity); headline numbers are stat tiles, not one-bar
 * charts. The number is always printed next to the bar, so nothing is
 * colour-only and the table view *is* the chart.
 */

function StatTile({ label, value, hint }) {
  return (
    <div className="tile">
      <span className="tile-value">{value}</span>
      <span className="tile-label">{label}</span>
      {hint && <span className="tile-hint">{hint}</span>}
    </div>
  )
}

function num(value) {
  if (!Number.isFinite(value)) return '—'
  const rounded = Math.round(value * 100) / 100
  return n(rounded)
}

function BreakdownRows({ top, rows, max, labelOf }) {
  return (
    <ol className="breakdown">
      {top.map((item, i) => (
        <li key={i} className="bd-row" title={`${labelOf(item)} — ${n(item.count)} rows (${pct(item.share)})`}>
          <span className="bd-label">{labelOf(item)}</span>
          <span className="bd-track">
            {/* Width is share of the largest value, so the longest bar fills the track. */}
            <span className="bd-bar" style={{ width: `${max > 0 ? (item.count / max) * 100 : 0}%` }} />
          </span>
          <span className="bd-count">{n(item.count)}</span>
          <span className="bd-share">{pct(item.share)}</span>
        </li>
      ))}
      {top.length === 0 && <li className="muted">No rows in scope.</li>}
    </ol>
  )
}

export function InsightsView({ result, columns, scanning, scopeLabel }) {
  if (!result) {
    return <div className="insights empty-state">Pick one or more columns to break down.</div>
  }
  const { rows } = result

  return (
    <div className="insights">
      <div className="insight-head">
        <div className="tiles">
          <StatTile label={`rows in scope · ${scopeLabel}`} value={n(rows)} />
          {result.columns.map((col) => (
            <StatTile
              key={col.name}
              label={`distinct ${col.name}`}
              value={`${col.distinctIsFloor ? '≥' : ''}${n(col.distinct)}`}
              hint={col.empty > 0 ? `${n(col.empty)} blank` : null}
            />
          ))}
        </div>
        {scanning && <span className="scan-label">scanning…</span>}
      </div>

      {result.combined && (
        <section className="card wide">
          <header className="card-head">
            <h3>{result.combined.columns.join(' + ')}</h3>
            <span className="card-meta">
              {result.combined.distinctIsFloor ? '≥' : ''}
              {n(result.combined.distinct)} combinations
            </span>
          </header>
          <BreakdownRows
            top={result.combined.top}
            rows={rows}
            max={result.combined.top[0]?.count ?? 0}
            labelOf={(item) => item.values.map((v) => (v === '' ? '(blank)' : v)).join(' · ')}
          />
        </section>
      )}

      <div className="card-grid">
        {result.columns.map((col) => (
          <section className="card" key={col.name}>
            <header className="card-head">
              <h3>{col.name}</h3>
              <span className="card-meta">
                {col.distinctIsFloor ? '≥' : ''}
                {n(col.distinct)} values
                {col.top.length < col.distinct ? ` · top ${col.top.length}` : ''}
              </span>
            </header>

            {col.numeric && (
              <div className="numeric-row">
                <span>
                  min <strong>{num(col.numeric.min)}</strong>
                </span>
                <span>
                  max <strong>{num(col.numeric.max)}</strong>
                </span>
                <span>
                  mean <strong>{num(col.numeric.mean)}</strong>
                </span>
                <span>
                  sum <strong>{num(col.numeric.sum)}</strong>
                </span>
              </div>
            )}

            <BreakdownRows
              top={col.top}
              rows={rows}
              max={col.top[0]?.count ?? 0}
              labelOf={(item) => (item.value === '' ? '(blank)' : item.value)}
            />

            {col.top.length < col.distinct && (
              <p className="card-foot">
                {n(rows - col.listed)} more rows across {col.distinctIsFloor ? '≥' : ''}
                {n(col.distinct - col.top.length)} other values
              </p>
            )}
          </section>
        ))}
      </div>
      {columns.length === 0 && <div className="empty-state">Pick a column in the sidebar.</div>}
    </div>
  )
}
